import { lazy } from "react";

import { Navigate, createBrowserRouter } from "react-router-dom";

import AppLayout from "@/layout";

import { PAGE_PATHS } from "@/constants";

/**
 * Har sahifa alohida chunk: panelga birinchi kirishda faqat boshqaruv
 * paneli yuklanadi, moderatsiya ekrani emas.
 *
 * Marshrutlarda rol tekshiruvi YO'Q va bu ataylab: menyu bandlarini
 * yashirish — qulaylik, chegara esa backend'da. Har bir sahifa o'zi
 * ishlatadigan endpoint 403 qaytarsa, xato toast'i ko'rinadi.
 */
const LoginPage = lazy(() => import("@/pages/login/page"));
const DashboardPage = lazy(() => import("@/pages/dashboard/page"));
const UsersPage = lazy(() => import("@/pages/users/page"));
const CategoriesPage = lazy(() => import("@/pages/categories/page"));
const CourseLevelsPage = lazy(() => import("@/pages/course-levels/page"));

// Moderatsiya
const CourseModerationPage = lazy(() => import("@/pages/course-moderation/page"));
const CourseModerationReviewPage = lazy(() => import("@/pages/course-moderation/review-page"));
const PostModerationPage = lazy(() => import("@/pages/post-moderation/page"));
const ReviewModerationPage = lazy(() => import("@/pages/review-moderation/page"));
const TeacherRequestsPage = lazy(() => import("@/pages/teacher-requests/page"));

// Kontent
const CoursesPage = lazy(() => import("@/pages/courses/page"));
const CourseWizardPage = lazy(() => import("@/pages/courses/wizard-page"));
const LessonContentPage = lazy(() => import("@/pages/courses/lesson-content-page"));
const LiveCoursesPage = lazy(() => import("@/pages/live-courses/page"));
const LiveCourseWizardPage = lazy(() => import("@/pages/live-courses/wizard-page"));
const NewsPage = lazy(() => import("@/pages/news/page"));
const NewsEditorPage = lazy(() => import("@/pages/news/editor-page"));

// Odamlar / pul / tizim
const ContactMessagesPage = lazy(() => import("@/pages/contact-messages/page"));
const PaymentsPage = lazy(() => import("@/pages/payments/page"));
const CardReviewPage = lazy(() => import("@/pages/card-review/page"));
const WithdrawalsPage = lazy(() => import("@/pages/withdrawals/page"));
const RefundRequestsPage = lazy(() => import("@/pages/refund-requests/page"));
const AnalyticsPage = lazy(() => import("@/pages/analytics/page"));
const AnnouncementsPage = lazy(() => import("@/pages/announcements/page"));
const AnnouncementNewPage = lazy(() => import("@/pages/announcements/new-page"));
const NotificationsPage = lazy(() => import("@/pages/notifications/page"));
const RemovalsPage = lazy(() => import("@/pages/removals/page"));
const LiveCourseManagePage = lazy(() => import("@/pages/live-courses/manage/page"));
const HealthPage = lazy(() => import("@/pages/health/page"));
const PlatformSettingsPage = lazy(() => import("@/pages/settings/page"));
const AiPage = lazy(() => import("@/pages/ai/page"));
const AiFlagsPage = lazy(() => import("@/pages/ai-flags/page"));

// Hisobotlar (faqat admin; moderator faqat o'z qarorlarini ko'radi)
const TeacherReportsPage = lazy(() => import("@/pages/reports/teachers/page"));
const TeacherReportPage = lazy(() => import("@/pages/reports/teachers/detail-page"));
const StudentReportsPage = lazy(() => import("@/pages/reports/students/page"));
const StudentReportPage = lazy(() => import("@/pages/reports/students/detail-page"));
const CourseReportsPage = lazy(() => import("@/pages/reports/courses/page"));
const AtRiskPage = lazy(() => import("@/pages/reports/at-risk/page"));
const ModeratorReportsPage = lazy(() => import("@/pages/reports/moderators/page"));
const ModeratorReportPage = lazy(() => import("@/pages/reports/moderators/detail-page"));
const ActivityLogPage = lazy(() => import("@/pages/reports/activity/page"));
const CohortsPage = lazy(() => import("@/pages/reports/cohorts/page"));
const PayoutsPage = lazy(() => import("@/pages/reports/payouts/page"));
const GradeChangesPage = lazy(() => import("@/pages/reports/grade-changes/page"));
const SecurityPage = lazy(() => import("@/pages/security/page"));
const ProfilePage = lazy(() => import("@/pages/profile/page"));
const NotFoundPage = lazy(() => import("@/pages/not-found/page"));

export const router = createBrowserRouter([
  { path: PAGE_PATHS.LOGIN, element: <LoginPage /> },
  {
    path: PAGE_PATHS.ROOT,
    element: <AppLayout />,
    children: [
      { index: true, element: <Navigate to={PAGE_PATHS.DASHBOARD} replace /> },
      { path: PAGE_PATHS.DASHBOARD, element: <DashboardPage /> },

      { path: PAGE_PATHS.COURSE_MODERATION, element: <CourseModerationPage /> },
      { path: PAGE_PATHS.COURSE_MODERATION_REVIEW, element: <CourseModerationReviewPage /> },
      { path: PAGE_PATHS.POST_MODERATION, element: <PostModerationPage /> },
      { path: PAGE_PATHS.AI_FLAGS, element: <AiFlagsPage /> },
      { path: PAGE_PATHS.REVIEW_MODERATION, element: <ReviewModerationPage /> },
      { path: PAGE_PATHS.TEACHER_REQUESTS, element: <TeacherRequestsPage /> },
      { path: PAGE_PATHS.REMOVALS, element: <RemovalsPage /> },
      { path: PAGE_PATHS.MY_DECISIONS, element: <ModeratorReportPage self /> },

      // `/courses/new` `/courses/:id` dan OLDIN turadi: aks holda "new"
      // identifikator deb o'qilardi.
      { path: PAGE_PATHS.COURSES, element: <CoursesPage /> },
      { path: PAGE_PATHS.COURSE_NEW, element: <CourseWizardPage /> },
      { path: PAGE_PATHS.LESSON_CONTENT, element: <LessonContentPage /> },
      { path: PAGE_PATHS.COURSE_EDIT, element: <CourseWizardPage /> },

      { path: PAGE_PATHS.LIVE_COURSES, element: <LiveCoursesPage /> },
      { path: PAGE_PATHS.LIVE_COURSE_NEW, element: <LiveCourseWizardPage /> },
      { path: PAGE_PATHS.LIVE_COURSE_EDIT, element: <LiveCourseWizardPage /> },
      { path: PAGE_PATHS.LIVE_COURSE_MANAGE, element: <LiveCourseManagePage /> },

      { path: PAGE_PATHS.NEWS, element: <NewsPage /> },
      { path: PAGE_PATHS.NEWS_NEW, element: <NewsEditorPage /> },
      { path: PAGE_PATHS.NEWS_EDIT, element: <NewsEditorPage /> },

      { path: PAGE_PATHS.USERS, element: <UsersPage /> },
      { path: PAGE_PATHS.CONTACT_MESSAGES, element: <ContactMessagesPage /> },

      { path: PAGE_PATHS.PAYMENTS, element: <PaymentsPage /> },
      { path: PAGE_PATHS.CARD_REVIEW, element: <CardReviewPage /> },
      { path: PAGE_PATHS.WITHDRAWALS, element: <WithdrawalsPage /> },
      { path: PAGE_PATHS.REFUND_REQUESTS, element: <RefundRequestsPage /> },
      { path: PAGE_PATHS.ANALYTICS, element: <AnalyticsPage /> },

      { path: PAGE_PATHS.REPORT_TEACHERS, element: <TeacherReportsPage /> },
      { path: PAGE_PATHS.REPORT_TEACHER, element: <TeacherReportPage /> },
      { path: PAGE_PATHS.REPORT_STUDENTS, element: <StudentReportsPage /> },
      { path: PAGE_PATHS.REPORT_STUDENT, element: <StudentReportPage /> },
      { path: PAGE_PATHS.REPORT_COURSES, element: <CourseReportsPage /> },
      { path: PAGE_PATHS.REPORT_AT_RISK, element: <AtRiskPage /> },
      { path: PAGE_PATHS.REPORT_MODERATORS, element: <ModeratorReportsPage /> },
      { path: PAGE_PATHS.REPORT_MODERATOR, element: <ModeratorReportPage /> },
      { path: PAGE_PATHS.REPORT_ACTIVITY, element: <ActivityLogPage /> },
      { path: PAGE_PATHS.REPORT_COHORTS, element: <CohortsPage /> },
      { path: PAGE_PATHS.REPORT_PAYOUTS, element: <PayoutsPage /> },
      { path: PAGE_PATHS.REPORT_GRADE_CHANGES, element: <GradeChangesPage /> },

      { path: PAGE_PATHS.CATEGORIES, element: <CategoriesPage /> },
      { path: PAGE_PATHS.COURSE_LEVELS, element: <CourseLevelsPage /> },
      { path: PAGE_PATHS.ANNOUNCEMENTS, element: <AnnouncementsPage /> },
      { path: PAGE_PATHS.ANNOUNCEMENT_NEW, element: <AnnouncementNewPage /> },
      { path: PAGE_PATHS.NOTIFICATIONS, element: <NotificationsPage /> },
      { path: PAGE_PATHS.HEALTH, element: <HealthPage /> },
      { path: PAGE_PATHS.PLATFORM_SETTINGS, element: <PlatformSettingsPage /> },
      { path: PAGE_PATHS.AI, element: <AiPage /> },

      { path: PAGE_PATHS.SECURITY, element: <SecurityPage /> },
      { path: PAGE_PATHS.PROFILE, element: <ProfilePage /> },

      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
